import { useMemo, useState } from 'react';
import { Plus, MoreHorizontal, Pencil, Copy, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/shared/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { useDashboardTabs } from '../hooks/useDashboardTabs';
import { DashboardTab } from '../types/DashboardRequests';

/**
 * Resolves a TranslatableText / string into the current locale's label.
 * Tabs created via the UI store plain strings; the bootstrap "Principal" tab
 * uses a default fallback when not localized.
 */
const resolveName = (name: DashboardTab['name'], locale: string): string => {
  if (typeof name === 'string') return name;
  if (!name) return '';
  return name[locale] ?? name.en ?? name.pt ?? Object.values(name).find(Boolean) ?? '';
};

/**
 * Horizontal tab bar shown above the dashboard grid. Renders only when the
 * user has opted into tabs (i.e. clicked "Adicionar aba" at least once).
 */
export const DashboardTabBar = () => {
  const { t, i18n } = useTranslation();
  const { tabs, currentTabId, hasTabs, setActiveTab, addTab, renameTab, deleteTab, duplicateTab } = useDashboardTabs();

  const [renameOpenFor, setRenameOpenFor] = useState<string | null>(null);
  const [deleteOpenFor, setDeleteOpenFor] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  const renameTarget = useMemo(
    () => tabs.find((tabItem) => tabItem.id === renameOpenFor),
    [tabs, renameOpenFor]
  );

  // Hide the bar entirely when no tabs were created — keeps the first visit clean.
  if (!hasTabs) return null;

  const handleAddTab = async () => {
    await addTab();
  };

  const openRename = (tabItem: DashboardTab) => {
    setDraftName(resolveName(tabItem.name, i18n.language));
    setRenameOpenFor(tabItem.id);
  };

  const handleRename = async () => {
    if (!renameOpenFor) return;
    await renameTab(renameOpenFor, draftName);
    setRenameOpenFor(null);
    setDraftName('');
  };

  const handleDuplicate = async (tabId: string) => {
    await duplicateTab(tabId);
  };

  const handleDelete = async () => {
    if (!deleteOpenFor) return;
    await deleteTab(deleteOpenFor);
    setDeleteOpenFor(null);
  };

  return (
    <>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b overflow-x-auto">
        {tabs.map((tabItem) => {
          const isActive = currentTabId === tabItem.id;
          return (
            <div
              key={tabItem.id}
              className={`group flex items-center gap-1 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
              }`}
            >
              <button
                type="button"
                onClick={() => setActiveTab(tabItem.id)}
                onDoubleClick={() => openRename(tabItem)}
                className="px-3 py-1.5 max-w-[180px] truncate"
                title={resolveName(tabItem.name, i18n.language)}
              >
                {resolveName(tabItem.name, i18n.language)}
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('dashboards.tabs.options', 'Opções da aba')}
                    className={`p-1 mr-0.5 rounded-md opacity-0 group-hover:opacity-100 ${
                      isActive ? 'opacity-70' : ''
                    } hover:bg-background/60 transition-opacity`}
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  <DropdownMenuItem onClick={() => openRename(tabItem)}>
                    <Pencil className="h-3.5 w-3.5 mr-2" />
                    {t('dashboards.tabs.rename', 'Renomear')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDuplicate(tabItem.id)}>
                    <Copy className="h-3.5 w-3.5 mr-2" />
                    {t('dashboards.tabs.duplicate', 'Duplicar')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDeleteOpenFor(tabItem.id)}
                    className="text-destructive"
                    disabled={tabs.length <= 1}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-2" />
                    {t('dashboards.tabs.delete', 'Excluir')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleAddTab}
          aria-label={t('dashboards.tabs.add', 'Adicionar aba')}
          title={t('dashboards.tabs.add', 'Adicionar aba')}
          className="h-7 w-7 ml-1 shrink-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={!!renameOpenFor} onOpenChange={(open) => !open && setRenameOpenFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboards.tabs.renameTitle', 'Renomear aba')}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
            }}
            placeholder={
              renameTarget
                ? resolveName(renameTarget.name, i18n.language)
                : t('dashboards.tabs.namePlaceholder', 'Nome da aba')
            }
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpenFor(null)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleRename} disabled={!draftName.trim()}>
              {t('common.save', 'Salvar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteOpenFor}
        onOpenChange={(open) => !open && setDeleteOpenFor(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('dashboards.tabs.deleteTitle', 'Excluir aba?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'dashboards.tabs.deleteDescription',
                'Esta ação não pode ser desfeita. Os dashboards e layouts desta aba serão perdidos.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('common.delete', 'Excluir')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
